import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Skull, UserPlus, UserMinus, Send, Trash2,
  Trophy, Calendar, Crown, MessageCircle, Sparkles, Edit3
} from 'lucide-react';
import { toast } from 'sonner';
import { useMafiaAuth } from '@/context/MafiaAuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicProfilePage() {
  const { memberNo } = useParams();
  const navigate = useNavigate();
  const { gamer: viewer } = useMafiaAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [followBusy, setFollowBusy] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [posting, setPosting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([
        fetch(`${API}/api/mafia/auth/profile/${memberNo}`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
        fetch(`${API}/api/mafia/auth/profile/${memberNo}/comments`).then(r => r.ok ? r.json() : { comments: [] }),
      ]);
      setProfile(p);
      setComments(c.comments || []);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberNo, viewer?.user_id]);

  const toggleFollow = async () => {
    if (!viewer) { navigate('/mafia/login'); return; }
    setFollowBusy(true);
    try {
      const method = profile.is_following ? 'DELETE' : 'POST';
      const r = await fetch(`${API}/api/mafia/auth/profile/${memberNo}/follow`, { method, credentials: 'include' });
      if (!r.ok) throw new Error('Action failed');
      await load();
    } catch (e) { toast.error(e.message); }
    finally { setFollowBusy(false); }
  };

  const postComment = async (e) => {
    e.preventDefault();
    if (!viewer) { navigate('/mafia/login'); return; }
    const body = newComment.trim();
    if (!body) return;
    setPosting(true);
    try {
      const r = await fetch(`${API}/api/mafia/auth/profile/${memberNo}/comments`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || 'Failed to post');
      }
      setNewComment('');
      await load();
    } catch (err) { toast.error(err.message); }
    finally { setPosting(false); }
  };

  const deleteComment = async (id) => {
    try {
      const r = await fetch(`${API}/api/mafia/auth/profile/${memberNo}/comments/${id}`, { method: 'DELETE', credentials: 'include' });
      if (!r.ok) throw new Error('Failed');
      await load();
    } catch (e) { toast.error(e.message); }
  };

  if (loading) return <div className="ms-page flex items-center justify-center ms-text-3">Loading…</div>;
  if (!profile) return (
    <div className="ms-page flex items-center justify-center">
      <div className="text-center max-w-sm">
        <div className="inline-flex items-center justify-center w-14 h-14 mx-auto mb-4" style={{ background: 'var(--ms-surface-2)', borderRadius: '999px' }}>
          <Skull size={22} className="ms-text-red" />
        </div>
        <h2 className="ms-display text-xl mb-2">Profile not found</h2>
        <p className="text-sm ms-text-2 mb-5">Either this player doesn't exist or their profile is private.</p>
        <button onClick={() => navigate('/mafia')} className="ms-btn ms-btn-secondary ms-btn-sm">Back to Salon</button>
      </div>
    </div>
  );

  const winRate = profile.games_played ? Math.round((profile.wins / profile.games_played) * 100) : 0;
  const avatar = profile.custom_avatar || profile.picture;

  return (
    <div className="ms-page" data-testid="public-profile-page">
      {/* Top bar */}
      <header className="sticky top-0 z-30 backdrop-blur-xl" style={{ background: 'rgba(12,10,9,0.75)', borderBottom: '1px solid var(--ms-border-subtle)' }}>
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="ms-btn ms-btn-ghost ms-btn-sm">
            <ArrowLeft size={14} /> Back
          </button>
          {viewer && !profile.is_self && (
            <button
              onClick={toggleFollow}
              disabled={followBusy}
              data-testid="profile-follow-btn"
              className={`ms-btn ${profile.is_following ? 'ms-btn-secondary' : 'ms-btn-primary'} disabled:opacity-50`}
            >
              {profile.is_following ? <><UserMinus size={13} /> Following</> : <><UserPlus size={13} /> Follow</>}
            </button>
          )}
          {profile.is_self && (
            <button onClick={() => navigate('/dashboard/settings')} data-testid="profile-edit-btn" className="ms-btn ms-btn-secondary ms-btn-sm">
              <Edit3 size={12} /> Edit profile
            </button>
          )}
        </div>
      </header>

      {/* Cover */}
      <div
        className="w-full h-56 sm:h-72 relative overflow-hidden"
        style={profile.cover_url
          ? { backgroundImage: `url(${profile.cover_url})`, backgroundSize: 'cover', backgroundPosition: 'center' }
          : { background: 'linear-gradient(135deg, #1a0606 0%, #0C0A09 50%, #1a0606 100%)' }
        }
        data-testid="profile-cover"
      >
        {!profile.cover_url && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Skull size={64} className="ms-text-red opacity-15" />
          </div>
        )}
        {/* Bottom-fade so avatar pops */}
        <div className="absolute inset-x-0 bottom-0 h-24" style={{ background: 'linear-gradient(to bottom, transparent, var(--ms-bg))' }} />
      </div>

      <main className="max-w-5xl mx-auto px-6 -mt-16 sm:-mt-20 relative z-10">
        {/* Header card */}
        <motion.section
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: 'easeOut' }}
          className="ms-card p-6 sm:p-8 flex flex-col sm:flex-row gap-6"
          data-testid="profile-header"
        >
          {/* Avatar */}
          <div className="shrink-0 -mt-14 sm:-mt-16">
            {avatar ? (
              <img
                src={avatar}
                alt={profile.name}
                className="w-28 h-28 sm:w-32 sm:h-32 object-cover"
                style={{ borderRadius: 'var(--ms-radius-lg)', border: '4px solid var(--ms-bg)', boxShadow: 'var(--ms-elev-card)' }}
              />
            ) : (
              <div
                className="w-28 h-28 sm:w-32 sm:h-32 flex items-center justify-center ms-display text-5xl ms-text-red"
                style={{ background: 'var(--ms-surface-2)', borderRadius: 'var(--ms-radius-lg)', border: '4px solid var(--ms-bg)', boxShadow: 'var(--ms-elev-card)' }}
              >
                {(profile.name || '?').slice(0, 1).toUpperCase()}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0 pt-2">
            <div className="flex items-start gap-3 flex-wrap">
              <h1 className="ms-display text-3xl sm:text-4xl leading-none truncate">{profile.name}</h1>
              <span className="ms-chip ms-chip-ember mt-2">
                <Sparkles size={11} /> {profile.rank}
              </span>
            </div>
            <p className="text-xs ms-text-3 mt-2">
              {profile.member_no} · Joined {profile.joined_at ? new Date(profile.joined_at).toLocaleDateString() : '—'}
            </p>
            {profile.bio && <p className="text-sm ms-text mt-4 whitespace-pre-wrap leading-relaxed max-w-2xl">{profile.bio}</p>}
            {profile.email && <p className="text-xs ms-text-2 mt-3">{profile.email}</p>}

            {/* Social stats */}
            <div className="flex items-center gap-6 mt-5 text-sm">
              <button onClick={() => navigate(`/p/${profile.member_no}/followers`)} data-testid="profile-followers-link" className="hover:ms-text-red transition-colors">
                <span className="font-semibold tabular-nums ms-text">{profile.followers_count}</span>
                <span className="ms-text-2 ml-1.5">followers</span>
              </button>
              <span className="ms-text-3">·</span>
              <button onClick={() => navigate(`/p/${profile.member_no}/following`)} data-testid="profile-following-link" className="hover:ms-text-red transition-colors">
                <span className="font-semibold tabular-nums ms-text">{profile.following_count}</span>
                <span className="ms-text-2 ml-1.5">following</span>
              </button>
            </div>
          </div>
        </motion.section>

        {/* Stats banner */}
        <section className="grid grid-cols-2 sm:grid-cols-4 mt-4 ms-card" data-testid="profile-stats">
          <Stat label="Points" value={profile.points} icon={Trophy} accent />
          <Stat label="Games" value={profile.games_played} icon={Calendar} />
          <Stat label="Wins" value={profile.wins} icon={Crown} />
          <Stat label="Win rate" value={`${winRate}%`} icon={Trophy} />
        </section>

        {/* Badges + recent matches */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
          <section className="ms-card">
            <PanelHeader title="Achievements" />
            {profile.badges.length === 0 ? (
              <EmptyText text="No badges yet." />
            ) : (
              <div className="p-4 space-y-2">
                {profile.badges.map((b) => (
                  <div
                    key={b.key}
                    data-testid={`pp-badge-${b.key}`}
                    className="flex items-start gap-3 p-3"
                    style={{
                      background: 'var(--ms-ember-soft)',
                      border: '1px solid rgba(224,200,149,0.25)',
                      borderRadius: 'var(--ms-radius-md)',
                    }}
                  >
                    <Crown size={14} className="ms-text-ember mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs font-semibold ms-text-ember">{b.label}</p>
                      <p className="text-[11px] ms-text-2 mt-0.5 leading-snug">{b.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="ms-card lg:col-span-2">
            <PanelHeader title="Recent matches" />
            {profile.matches.length === 0 ? (
              <EmptyText text="No matches recorded." />
            ) : (
              <div className="ms-divide">
                {profile.matches.map((m) => (
                  <div key={m.game_id} className="grid grid-cols-12 items-center px-5 py-3 hover:bg-[var(--ms-surface-2)] transition-colors" data-testid={`pp-match-${m.game_id}`}>
                    <span className="col-span-4 text-xs ms-text-3">
                      {m.created_at ? new Date(m.created_at).toLocaleDateString() : '—'}
                    </span>
                    <span className="col-span-3 text-sm ms-text">Seat #{m.seat}</span>
                    <span className="col-span-3 text-xs ms-text-2 capitalize">{m.team || '—'}</span>
                    <span className="col-span-2 text-right">
                      <span className={`ms-chip ${m.won ? 'ms-chip-red' : ''}`}>
                        {m.status === 'ended' ? (m.won ? 'Win' : 'Loss') : m.status}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        {/* Comment wall */}
        <section className="ms-card mt-4 mb-16" data-testid="profile-comments">
          <PanelHeader title={`Wall · ${comments.length} ${comments.length === 1 ? 'comment' : 'comments'}`} icon={MessageCircle} />
          {viewer ? (
            <form onSubmit={postComment} className="px-5 py-4 ms-border-b flex gap-3">
              <input
                value={newComment}
                onChange={(e) => setNewComment(e.target.value.slice(0, 600))}
                placeholder={profile.is_self ? 'Leave a note for visitors…' : `Write something to ${(profile.name || '').split(' ')[0]}…`}
                data-testid="comment-input"
                className="ms-input flex-1"
              />
              <button type="submit" disabled={posting || !newComment.trim()} data-testid="comment-submit" className="ms-btn ms-btn-primary disabled:opacity-50">
                <Send size={12} /> {posting ? 'Posting…' : 'Post'}
              </button>
            </form>
          ) : (
            <div className="px-5 py-4 ms-border-b text-sm ms-text-2">
              <button onClick={() => navigate('/mafia/login')} className="ms-text-red hover:underline font-medium">Sign in</button> to leave a comment.
            </div>
          )}
          {comments.length === 0 ? (
            <EmptyText text="Be the first to write something." />
          ) : (
            <div className="ms-divide">
              {comments.map((c) => {
                const isMine = viewer && c.author.member_no === viewer.member_no;
                const isOwner = profile.is_self;
                return (
                  <div key={c.id} className="px-5 py-4 flex gap-3" data-testid={`comment-${c.id}`}>
                    {(c.author.custom_avatar || c.author.picture) ? (
                      <img src={c.author.custom_avatar || c.author.picture} alt={c.author.name} className="w-9 h-9 object-cover shrink-0" style={{ borderRadius: 'var(--ms-radius-md)', border: '1px solid var(--ms-border-strong)' }} />
                    ) : (
                      <div className="w-9 h-9 flex items-center justify-center ms-text-red font-semibold shrink-0" style={{ background: 'var(--ms-surface-2)', borderRadius: 'var(--ms-radius-md)', border: '1px solid var(--ms-border-strong)' }}>
                        {(c.author.name || '?').slice(0, 1).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <button onClick={() => navigate(`/p/${c.author.member_no}`)} className="text-sm font-semibold ms-text hover:ms-text-red truncate">
                          {c.author.name}
                        </button>
                        <span className="text-[11px] ms-text-3">
                          {c.created_at ? new Date(c.created_at).toLocaleString() : ''}
                        </span>
                      </div>
                      <p className="text-sm ms-text mt-1 whitespace-pre-wrap break-words leading-relaxed">{c.body}</p>
                    </div>
                    {(isMine || isOwner) && (
                      <button onClick={() => deleteComment(c.id)} data-testid={`comment-delete-${c.id}`} className="shrink-0 ms-text-3 hover:text-rose-400" title="Delete">
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function PanelHeader({ title, icon: Icon }) {
  return (
    <div className="px-5 py-4 ms-border-b flex items-center gap-2">
      {Icon ? <Icon size={13} className="ms-text-red" /> : <span className="w-1.5 h-1.5" style={{ background: 'var(--ms-red)', borderRadius: '999px' }} />}
      <p className="ms-kicker">{title}</p>
    </div>
  );
}

function EmptyText({ text }) {
  return <p className="px-5 py-8 text-sm ms-text-2 text-center">{text}</p>;
}

function Stat({ label, value, icon: Icon, accent }) {
  return (
    <div className="px-6 py-6 ms-border-r last:border-r-0">
      <div className="flex items-center justify-between mb-2">
        <p className="ms-kicker">{label}</p>
        <Icon size={14} className={accent ? 'ms-text-red' : 'ms-text-3'} />
      </div>
      <p className={`ms-display text-3xl sm:text-4xl tabular-nums ${accent ? 'ms-text-red' : ''}`}>{value}</p>
    </div>
  );
}
